import { forwardRef, ForwardedRef } from 'preact/compat';
import { useMemo, useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { RefObject } from 'preact';
import { PositionData, Coords } from './Floating.types';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { FloatingStyles } from './themes/FloatingStyles.css';
import { classNames } from '../utils/UNSAFE_classNames';
import { mergeRefs } from '../utils/PRIVATE_refUtils';
import type { ColorProps } from '../utils/UNSAFE_interpolations/colors';
import { Property } from 'csstype';
import { rgba } from '../UNSAFE_Theme/utils';
import { colorSchemeVars } from '@oracle/oraclejet-internal-theme-contract/themeContract.css';
import { FloatingRedwoodTheme } from './themes/redwood/FloatingTheme';

type Props = Pick<ColorProps, 'backgroundColor'> & {
  data?: PositionData;
  anchorRef: RefObject<Element | Coords>;
  tail: 'none' | 'simple';
  isRtl: boolean;
};

const ARROW_SIZE = 7;
const ARROW_SIZE_CORNER = 6;
const ARROW_INNER_SIZE = 5;
const ARROW_INNER_SIZE_CORNER = 5;

const FloatingTail = forwardRef(
  (
    { data, anchorRef, tail, isRtl, backgroundColor }: Props,
    ref: ForwardedRef<HTMLDivElement | null>
  ) => {
    const { styles, baseTheme } = useComponentTheme<never, FloatingStyles>(FloatingRedwoodTheme);
    const arrowRef = useRef<HTMLDivElement | null>(null);
    const [arrowStyles, setArrowStyles] = useState({
      arrowStyle: { visibility: 'hidden' },
      arrowInnerStyle: { visibility: 'hidden' }
    });

    useLayoutEffect(() => {
      if (arrowRef.current == null) return;
      const { isCornerVerticalPlacement, isCornerHorizontalPlacement } = isCorner(
        anchorRef,
        tail,
        data
      );
      setArrowStyles(
        setStyles(
          tail,
          isRtl,
          isCornerVerticalPlacement,
          isCornerHorizontalPlacement,
          data,
          backgroundColor
        )
      );
    }, [
      data,
      backgroundColor,
      data?.placement,
      data?.x,
      data?.y,
      data?.offset,
      data?.arrow,
      tail,
      isRtl,
      anchorRef
    ]);

    const stableRef = useMemo(() => mergeRefs(ref, arrowRef), [ref, arrowRef]);
    return (
      tail === 'simple' && (
        <div
          ref={stableRef}
          class={classNames([baseTheme, styles.floatingTailBaseStyle])}
          style={arrowStyles.arrowStyle}>
          {' '}
          {/*Outer arrow - shadow*/}
          <div
            class={classNames([styles.floatingTailBaseStyle])}
            style={arrowStyles.arrowInnerStyle}></div>{' '}
          {/*Inner arrow - body*/}
        </div>
      )
    );
  }
);

function isCorner(
  anchorRef: RefObject<Element | Coords>,
  tail: 'none' | 'simple',
  data?: PositionData
) {
  if (tail === 'simple' && data && data.arrow) {
    const { centerOffset } = data.arrow;
    const arrowSize = ARROW_SIZE;
    let anchorRefHeight = 0;
    let anchorRefWidth = 0;
    const current = anchorRef.current;
    // @ts-ignore. Type 'Element | Coords' issues
    // Property 'x' does not exist on type 'Element'.
    // Property 'y' does not exist on type 'Element'.
    const isAnchorRefElement = current?.x === undefined && current?.y === undefined;
    if (isAnchorRefElement) {
      // @ts-ignore. Property 'getBoundingClientRect' does not exist on type 'Coords'.
      anchorRefHeight = Math.round(current.getBoundingClientRect().height);
      // @ts-ignore. Property 'getBoundingClientRect' does not exist on type 'Coords'.
      anchorRefWidth = Math.round(anchorRef.current?.getBoundingClientRect().width);
    }
    const isCornerVerticalPlacement = isAnchorRefElement
      ? Math.abs(centerOffset) >= anchorRefHeight / 2 - arrowSize / 2
      : ['start-top-corner', 'start-bottom-corner', 'end-top-corner', 'end-bottom-corner'].includes(
          data.origPlacement
        );
    const isCornerHorizontalPlacement = isAnchorRefElement
      ? Math.abs(centerOffset) >= anchorRefWidth / 2 - arrowSize / 2
      : ['top-start-corner', 'top-end-corner', 'bottom-start-corner', 'bottom-end-corner'].includes(
          data.origPlacement
        );

    return { isCornerVerticalPlacement, isCornerHorizontalPlacement };
  } else return { isCornerVerticalPlacement: false, isCornerHorizontalPlacement: false };
}

function setStyles(
  //arrowEl: HTMLDivElement,
  tail: 'none' | 'simple',
  isRtl: boolean,
  isCornerVerticalPlacement: boolean,
  isCornerHorizontalPlacement: boolean,
  data?: PositionData,
  backgroundColor?: Property.BackgroundColor
) {
  const result = { visibility: 'visible' };
  const innerResult = { visibility: 'visible' };
  if (tail === 'simple' && data && data.arrow) {
    const { x, y, centerOffset } = data.arrow;
    const arrowSize = ARROW_SIZE;
    const arrowSizeCorner = ARROW_SIZE_CORNER;
    //const arrowInnerEl = arrowEl.querySelector('div') as Element;
    const arrowInnerSize = ARROW_INNER_SIZE;
    const arrowInnerSizeCorner = ARROW_INNER_SIZE_CORNER;

    const isCenter = centerOffset === 0;
    const isCenterOffsetPositive = centerOffset > 0;

    // Set sizes of the arrow
    setArrowSizes(isCornerVerticalPlacement || isCornerHorizontalPlacement);

    // Corner vs. normal arrow
    function setArrowSizes(isCornerPlacement?: boolean) {
      // Outer arrow (shadow)
      const arrowFinalSize = isCornerPlacement ? arrowSizeCorner : arrowSize;
      setArrowStyle(
        {
          // Tracked: 
          borderTop: `${getSize(arrowFinalSize)} solid ${rgba(
            colorSchemeVars.palette.neutral[90],
            0.1
          )}`,
          borderRight: getTransparentBorder(arrowFinalSize),
          borderLeft: getTransparentBorder(arrowFinalSize)
        },
        result
      );
      // Inner arrow
      const arrowInnerFinalSize = isCornerPlacement ? arrowInnerSizeCorner : arrowInnerSize;
      setArrowInnerStyle(
        {
          // Tracked: 
          borderTop: `${getSize(arrowInnerFinalSize)} solid ${backgroundColor}`,
          borderRight: `${getTransparentBorder(arrowInnerFinalSize)}`,
          borderLeft: `${getTransparentBorder(arrowInnerFinalSize)}`
        },
        innerResult
      );
    }

    function setArrowStyle(style: object, result: object) {
      Object.assign(result, style);
    }

    function setArrowInnerStyle(style: object, result: object) {
      Object.assign(result, style);
    }

    function getTurn(eighth: number) {
      return `rotate(${eighth * 45}deg)`;
    }

    function getSize(size: number) {
      return `${size}px`;
    }

    function getTransparentBorder(size: number) {
      return `${getSize(size)} solid transparent`;
    }

    const placementArr = data.placement.split('-');
    const side = placementArr[0];

    switch (side) {
      case 'top':
        setArrowTop();
        break;
      case 'end':
        isRtl ? setArrowStart() : setArrowEnd();
        break;
      case 'bottom':
        setArrowBottom();
        break;
      case 'start':
        isRtl ? setArrowEnd() : setArrowStart();
        break;
    }

    function setArrowTop() {
      if (x != undefined) {
        if (isCornerHorizontalPlacement) {
          // Top corners
          setArrowStyle(
            {
              top: '', // flip 'reset'
              bottom: getSize(-arrowSize + 2),
              left: getSize(x + (isCenterOffsetPositive ? 5 : -5)),
              transform: getTurn(isCenterOffsetPositive ? 5 : 3)
            },
            result
          );
          setCornerArrowInner();
        } else {
          setArrowStyle(
            {
              top: '', // flip 'reset'
              bottom: getSize(-arrowSize),
              left: isCenter ? getSize(x) : getSize(x + (isCenterOffsetPositive ? 1 : -1)),
              transform: getTurn(0)
            },
            result
          );
          setNonCornerArrowInner();
        }
      }
    }

    function setArrowEnd() {
      if (y != undefined) {
        if (isCornerVerticalPlacement) {
          // End corners
          setArrowStyle(
            {
              top: getSize(y + (isCenterOffsetPositive ? 2 : -2)),
              right: '', // flip 'reset'
              left: getSize(-arrowSize - 1),
              transform: getTurn(isCenterOffsetPositive ? 7 : 5)
            },
            result
          );
          setCornerArrowInner();
        } else {
          // End
          setArrowStyle(
            {
              top: isCenter ? getSize(y) : getSize(y + (isCenterOffsetPositive ? -2 : +2)),
              right: '', // flip 'reset'
              left: getSize(-arrowSize * 1.5),
              transform: getTurn(2)
            },
            result
          );
          setNonCornerArrowInner();
        }
      }
    }

    function setArrowBottom() {
      if (x != undefined) {
        if (isCornerHorizontalPlacement) {
          // Bottom corners
          setArrowStyle(
            {
              top: getSize(-arrowSize + 3),
              bottom: '', // flip 'reset'
              left: getSize(x + (isCenterOffsetPositive ? 4 : -4)),
              transform: getTurn(isCenterOffsetPositive ? 2 : 6)
            },
            result
          );
          setArrowInnerStyle(
            {
              top: getSize(-arrowInnerSize),
              left: getSize(-arrowInnerSize + (isCenterOffsetPositive ? 1 : -1))
            },
            innerResult
          );
        } else {
          setArrowStyle(
            {
              top: getSize(-arrowSize),
              bottom: '', // flip 'reset'
              left: isCenter ? getSize(x) : getSize(x + (isCenterOffsetPositive ? 1 : -1)),
              transform: getTurn(4)
            },
            result
          );
          setNonCornerArrowInner();
        }
      }
    }

    function setArrowStart() {
      if (y != undefined) {
        if (isCornerVerticalPlacement) {
          // Start corners
          setArrowStyle(
            {
              top: getSize(y + (isCenterOffsetPositive ? 2 : -2)),
              right: getSize(-arrowSize - 1),
              left: '', // flip 'reset'
              transform: getTurn(isCenterOffsetPositive ? 1 : 3)
            },
            result
          );
          setArrowInnerStyle(
            {
              top: getSize(-arrowInnerSize),
              left: getSize(-arrowInnerSize + (isCenterOffsetPositive ? -2 : 2))
            },
            innerResult
          );
        } else {
          // Start
          setArrowStyle(
            {
              top: isCenter ? getSize(y) : getSize(y + (isCenterOffsetPositive ? -2 : +2)),
              right: getSize(-arrowSize * 1.5),
              left: '', // flip 'reset'
              transform: getTurn(6)
            },
            result
          );
          setNonCornerArrowInner();
        }
      }
    }

    function setCornerArrowInner() {
      setArrowInnerStyle(
        {
          top: getSize(-arrowInnerSize),
          left: getSize(-arrowInnerSize + (isCenterOffsetPositive ? 2 : -2))
        },
        innerResult
      );
    }

    function setNonCornerArrowInner() {
      setArrowInnerStyle(
        {
          top: getSize(-arrowInnerSize - 2),
          left: getSize(-arrowInnerSize)
        },
        innerResult
      );
    }
  }
  return { arrowStyle: result, arrowInnerStyle: innerResult };
}

export { FloatingTail };
